/* eslint-disable react-hooks/exhaustive-deps */
import { Search } from "@mui/icons-material";
import {
  Alert,
  Button,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import UserWarehouseApi from "../../../api/UserWarehouse/UserWarehouseApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";

const PAGE_SIZE = 20;

const WarehouseMapping: React.FC = () => {
  const { setLoading } = useLoading();
  const [users, setUsers] = useState<Array<any>>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [userPage, setUserPage] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<any>>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showError = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const getErrorMessage = (error: unknown, fallbackMessage: string) => {
    if (axios.isAxiosError(error)) {
      return (
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        fallbackMessage
      );
    }

    if (error instanceof Error) {
      return error.message || fallbackMessage;
    }

    return fallbackMessage;
  };

  const fetchUsers = async (page: number, append: boolean) => {
    if (isLoadingUsers) return;

    try {
      setIsLoadingUsers(true);
      if (page === 0 && !append) {
        setLoading(true);
      }

      const res = await UserWarehouseApi.api.getUserList(
        "ROLE_INVENTORY_MANAGER",
        undefined,
        page,
        PAGE_SIZE
      );
      const responseData = res?.data ?? res;
      const userList = responseData?.userList || responseData?.data?.userList || [];

      if (Array.isArray(userList)) {
        console.debug("WarehouseMapping: fetched users", userList);
        const normalized = userList.map((u: any) => ({
          id: u.id ?? u.userId ?? u.user_id,
          name:
            u.name ??
            u.fullName ??
            u.username ??
            u.email ??
            (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : "Unknown"),
        }));

        setUsers((prev) => {
          if (!append || page === 0) {
            return normalized;
          }

          const existingIds = new Set(prev.map((user) => user.id));
          const uniqueNewUsers = normalized.filter((user) => !existingIds.has(user.id));
          return [...prev, ...uniqueNewUsers];
        });

        const responseHasNext = responseData?.hasNext ?? responseData?.data?.hasNext;
        const responseIsLast = responseData?.isLast ?? responseData?.data?.isLast;
        setHasMoreUsers(
          responseHasNext ?? (responseIsLast == null ? normalized.length === PAGE_SIZE : !responseIsLast)
        );
      } else {
        setHasMoreUsers(false);
        if (!append || page === 0) {
          setUsers([]);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingUsers(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    setUserPage(0);
    setHasMoreUsers(false);
    void fetchUsers(0, false);
  }, []);

  useEffect(() => {
    if (userPage === 0) return;
    void fetchUsers(userPage, true);
  }, [userPage]);

  const fetchWarehouses = async () => {
    if (selectedUser == null) {
      setWarehouses([]);
      setHasNextPage(false);
      return;
    }

    try {
      setIsLoadingWarehouses(true);
      const res = await UserWarehouseApi.api.getUserWarehouses(
        selectedUser,
        currentPage,
        PAGE_SIZE,
        debouncedSearchQuery
      );
      const responseData = res?.data ?? res;
      const warehouseList =
        responseData?.userWarehouseMappingList ||
        responseData?.data?.userWarehouseMappingList ||
        [];

      if (Array.isArray(warehouseList)) {
        setWarehouses(warehouseList);
      } else {
        setWarehouses([]);
      }

      setHasNextPage(
        responseData?.hasNext ??
        responseData?.data?.hasNext ??
        !(responseData?.isLast ?? responseData?.data?.isLast ?? false)
      );
    } catch (error) {
      console.error(error);
      showError(getErrorMessage(error, "Failed to load warehouse mapping"));
      setWarehouses([]);
      setHasNextPage(false);
    } finally {
      setIsLoadingWarehouses(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    console.debug("WarehouseMapping: selectedUser changed", selectedUser);
    const timer = window.setTimeout(() => {
      fetchWarehouses();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [selectedUser, currentPage, debouncedSearchQuery]);

  const handleAssignToggle = async (warehouseId: number, currentlyAssigned: boolean) => {
    if (selectedUser == null) return;
    try {
      setLoading(true);
      await UserWarehouseApi.api.assignWarehouse(selectedUser, warehouseId, !currentlyAssigned);
      setWarehouses((prev) => prev.map((w) => (w.warehouseId === warehouseId ? { ...w, isAssigned: !currentlyAssigned } : w)));
    } catch (error) {
      console.error(error);
      showError(getErrorMessage(error, "Failed to update warehouse assignment"));
    }
    setLoading(false);
  };

  const handleUserChange = (value: string) => {
    setSelectedUser(value ? Number(value) : null);
    setCurrentPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(0);
  };

  const handleUserScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLElement;
    const threshold = 5;

    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - threshold &&
      hasMoreUsers &&
      !isLoadingUsers
    ) {
      setUserPage((prev) => prev + 1);
    }
  };

  return (
    <>
    <div className="flex min-h-screen bg-[#F5F7FB]">
      <div className="w-64 flex-shrink-0 bg-white shadow-md">
        <SideBarNavigation />
      </div>
      <div className="flex-1 p-6 ml-8 bg-[#F8FAFC]">
        <h1 className="text-3xl font-semibold mb-6 text-left">Warehouse Mapping</h1>

        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="w-full max-w-[300px]">
            <TextField
              select
              fullWidth
              label="Inventory Managers"
              value={selectedUser || ""}
              onChange={(e) => handleUserChange(e.target.value)}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    onScroll: handleUserScroll,
                    sx: {
                      maxHeight: 200,
                      overflowY: "auto",
                    },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 50,
                  borderRadius: "6px",
                  backgroundColor: "#fff",
                  "& fieldset": {
                    borderColor: "#1D7AF2",
                    borderWidth: "2px"
                  },
                  "&:hover fieldset": {
                    borderColor: "#1D7AF2"
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1D7AF2"
                  }
                },
                "& .MuiInputLabel-root": {
                  color: "#1D7AF2",
                  fontSize: "15px",
                  top: 2,
                  left: 2
                },
                "& .MuiInputLabel-shrink": {
                  top: 0,
                  left: 2,
                  backgroundColor: "#fff",
                  px: 0.5
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  fontSize: "17px",
                  color: "#1F2937",
                  paddingTop: "18px !important",
                  paddingBottom: "12px !important"
                }
              }}
            >
              {users && users.length > 0 ? (
                users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No users available</MenuItem>
              )}
            </TextField>
          </div>

          <div className="w-full max-w-[360px] lg:pt-[2px]">
            <TextField
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              disabled={selectedUser == null}
              placeholder="Search by warehouse name"
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 46,
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  "& fieldset": {
                    borderColor: "#E9E9E9",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#D1D5DB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#D1D5DB",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "10px 12px",
                  fontSize: "15px",
                  color: "#111827",
                },
                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                  color: "#9CA3AF",
                  fontSize: 20,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>

        <div className="rounded-[12px] border border-[#E6ECF3] bg-white px-5 py-5 shadow-[0_1px_4px_rgba(16,24,40,0.04)]">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-left text-[20px] font-semibold text-[#111827]">
              Hub List
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead className="text-left text-[14px] font-medium text-[#7A869A]">
                <tr>
                  <th className="px-4 py-3 font-normal">#</th>
                  <th className="px-4 py-3 font-normal">Warehouse ID</th>
                  <th className="px-4 py-3 text-center font-normal">Warehouse Name</th>
                  <th className="px-4 py-3 text-left font-normal">Warehouse Code</th>
                  <th className="px-4 py-3 text-center font-normal">Assign Hub</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingWarehouses ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-[14px] text-[#7A869A]">
                      Loading warehouses...
                    </td>
                  </tr>
                ) : warehouses.map((w, idx) => (
                  <tr key={w.warehouseId} className="border-t border-[#EDF2F7]">
                    <td className="px-4 py-4 text-[14px] text-[#111827]">{currentPage * PAGE_SIZE + idx + 1}</td>
                    <td className="px-4 py-4 text-[14px] text-[#111827]">{w.warehouseId}</td>
                    <td className="px-4 py-4 text-[14px] text-[#111827] max-w-xs break-words">{w.warehouseName}</td>
                    <td className="px-4 py-4 text-[14px] font-semibold text-[#111827]">{w.warehouseCode || "-"}</td>
                    <td className="px-4 py-4 text-center">
                      <Button
                        variant={w.isAssigned ? "contained" : "outlined"}
                        onClick={() => handleAssignToggle(w.warehouseId, w.isAssigned)}
                        sx={{
                          minWidth: 86,
                          height: 36,
                          px: 2,
                          borderRadius: "999px",
                          textTransform: "none",
                          fontSize: "14px",
                          fontWeight: 600,
                          boxShadow: "none",
                          color: w.isAssigned ? "#fff" : "#1D7AF2",
                          bgcolor: w.isAssigned ? "#1D7AF2" : "transparent",
                          borderColor: "#1D7AF2",
                          "&:hover": {
                            bgcolor: w.isAssigned ? "#1769d8" : "rgba(29,122,242,0.06)",
                            borderColor: "#1D7AF2",
                            boxShadow: "none"
                          }
                        }}
                      >
                        {w.isAssigned ? "Assigned" : "Assign"}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!isLoadingWarehouses && warehouses.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-[14px] text-[#7A869A]">
                      {selectedUser == null
                        ? "Select a user to view warehouse mapping"
                        : "No warehouses found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t border-[#EDF2F7] pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-[#7A869A]">
              Page {currentPage + 1} · {PAGE_SIZE} rows per page
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outlined"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={selectedUser == null || currentPage === 0 || isLoadingWarehouses}
                sx={{
                  minWidth: 88,
                  height: 34,
                  borderRadius: "999px",
                  textTransform: "none",
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                Prev
              </Button>
              <Button
                variant="contained"
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={selectedUser == null || !hasNextPage || isLoadingWarehouses}
                sx={{
                  minWidth: 88,
                  height: 34,
                  borderRadius: "999px",
                  textTransform: "none",
                  bgcolor: "#1D7AF2",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#1769d8",
                    boxShadow: "none",
                  },
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={4000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setSnackbarOpen(false)}
        severity="error"
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default WarehouseMapping;
